import React, { useEffect, useState } from 'react'
import { useReactiveVar } from '@apollo/client'

import { memoryCurrentGame } from './lib'
import { updateGameToStorage, returnGameFromStorage, returnGameProgress, updateGameMeta, gameOutcome, MISTAKE_LIMIT } from './logic'
import { checkSudoku, isComplete, findClash, describeClash } from './lib/sudoku'

import Board from './Board'
import CellMenu from './CellMenu'
// 🔴 "SHOW SOLUTION" IS OFF FOR NOW (George, 22 September 2026: "remove show solution,
// comment it out for now"). The button and the import are COMMENTED, not deleted, so
// restoring it is one un-comment in each place. With the button gone nothing can open the
// solution modal in Game.js, so the feature is unreachable while the machinery stays intact.
// import ShowSolutionButton from './ShowSolutionButton'

import { Row, Col, Progress, Typography, Space, Card } from 'antd'

const { Title } = Typography

const Play = ({ showSolution, setShowSolution }) => {
  const gameId = useReactiveVar(memoryCurrentGame)

  const [game, setGame] = useState()
  // The cell whose menu is open (null = no menu), and the digits the game has REJECTED as
  // cell index -> digit. A rejected digit is deliberately NOT written into `puzzle`: that
  // is what the progress bar, the completion check and hasConflicts all read, so a wrong
  // digit living there would make every later guess read as "Incorrect" and the puzzle
  // unwinnable. Board draws it instead, in red, until it is cleared.
  const [menuCell, setMenuCell] = useState(null)
  const [mistakes, setMistakes] = useState({})
  // 🔴 THE MISTAKE TALLY (George, 22 September 2026: "we need to keep track of mistakes. if
  // they make 3 mistakes, they can continue to 6, then 9"). It counts EVERY rejected digit,
  // it never goes down — clearing a cell does not buy a mistake back — and it is banked into
  // the game itself, so it survives a reload and the saved list can show it.
  const [mistakeCount, setMistakeCount] = useState(0)

  useEffect(() => {
    setMenuCell(null)
    setMistakes({})
    if (gameId) {
      const stored = returnGameFromStorage(gameId)
      setGame(stored)
      setMistakeCount(stored && stored.mistakeCount ? stored.mistakeCount : 0)
    }
  }, [gameId])

  if (!game) return null

  // 🔴 THE TOP ROW OF NUMBERS IS GONE, AND SO IS THE HIGHLIGHTING (George, 22 September
  // 2026: "i dont like any of that highlighting just remove it and remove the top row of
  // numbers. instead when i click on a black cell, open a menu of 9 numbers i can put
  // there"). Clicking an empty cell opens that menu; the digit is chosen there.
  const openMenu = cell => {
    window.siteTrack?.('cell_open', { label: gameId })
    setMenuCell(cell)
  }

  const place = value => {
    const cell = menuCell
    if (!cell) return

    const { puzzle } = game
    const submit = [...puzzle]
    submit[cell.index] = value - 1

    const { correct, puzzleFormatted, hints } = checkSudoku(submit, true)

    if (!correct) {
      const nextCount = mistakeCount + 1

      window.siteTrack?.('guess_incorrect', { label: gameId, mistakeCount: nextCount })

      setMistakeCount(nextCount)
      updateGameMeta(gameId, { mistakeCount: nextCount })

      // 🔴 NO TOAST (George, 22 September 2026: "i dont like the toasts. especially the one that
      // says you can keep playing"). Nothing is announced here any more. The tally in the card
      // header counts the mistake and turns red at the limit, and the menu below already says
      // exactly why the digit was refused — a banner saying the same thing on top of that was
      // noise, and the one about carrying on was noise about a rule the player had not asked
      // about yet.
      //
      // The menu STAYS OPEN — the same menu, which now carries the reason AND "Clear this
      // cell". George's question of 22 September 2026 — "how can 7 not go there?" — was
      // answerable and was not answered: the site refused the move and said nothing about
      // which row, column or box already held the 7. Now it names it.
      const reason = describeClash(findClash(submit, cell.index, value - 1), value)
      setMistakes(m => ({ ...m, [cell.index]: { value, reason } }))
      return
    }

    window.siteTrack?.('guess_correct', { label: gameId })

    updateGameToStorage(gameId, submit, puzzleFormatted, hints)
    setGame(returnGameFromStorage(gameId))
    setMistakes(m => {
      const next = { ...m }
      delete next[cell.index]
      return next
    })
    setMenuCell(null)

    if (isComplete(submit)) {
      // the result is DERIVED, not stored: the header and the saved list both read it from the
      // finished board and the tally, so they cannot disagree with each other
      window.siteTrack?.('game_completed', { label: gameId, outcome: gameOutcome({ percent: 100, mistakeCount }) })
    }
  }

  const clearCell = () => {
    if (!menuCell) return
    window.siteTrack?.('cell_clear', { label: gameId })
    setMistakes(m => {
      const next = { ...m }
      delete next[menuCell.index]
      return next
    })
    setMenuCell(null)
  }

  const { puzzleFormatted } = game
  const { percent, progressType } = returnGameProgress(game)
  // 🔴 THE RESULT IS DERIVED, NEVER STORED. A finished puzzle is a win only under the mistake
  // limit (George, 22 September 2026: "if they make 3 mistakes, its not considered a win"), and
  // the header, the saved list and the completion event all read the same function — so they
  // cannot disagree, and a reload cannot stale it.
  const outcome = percent === 100 ? gameOutcome({ percent, mistakeCount }) : null
  // written as a plain object rather than an inline ternary so the header stays flat — a ternary
  // spread over five JSX lines is what `standard` refuses (`multiline-ternary`)
  const outcomeBadge = outcome
    ? { text: outcome === 'win' ? 'WIN' : 'LOSE', color: outcome === 'win' ? '#a3e635' : '#f87171' }
    : null

  return (
    <Card
      className='dvs-board-card'
      type='inner'
      title={(
        <Row
          justify='space-between'
        >
          <Col>
            <Title level={4} style={{ margin: 0, color: '#a3e635' }}>
              {gameId}
              {game.difficulty ? <span style={{ color: '#8b98a5', fontWeight: 400 }}>{' · '}{game.difficulty}</span> : null}
              {/* 🔴 THE SEPARATOR IS WRITTEN OUT (George, 22 September 2026: "the horizonal
                  spacinghere is wrong after the game type"). It rendered as
                  "SocialBerriesStay · Absurd· 3 mistakes" because JSX trims the whitespace at
                  the start of a line, so the `·` lost the space in front of it. An explicit
                  `{' · '}` cannot be trimmed. */}
              <span style={{ color: mistakeCount >= MISTAKE_LIMIT ? '#f87171' : '#8b98a5', fontWeight: 400 }}>
                {' · '}{mistakeCount} {mistakeCount === 1 ? 'mistake' : 'mistakes'}
              </span>
              {outcomeBadge && (
                <span style={{ color: outcomeBadge.color, fontWeight: 700 }}>
                  {' · '}{outcomeBadge.text}
                </span>
              )}
            </Title>
          </Col>
          <Col>
            {/* Show solution is commented out for now — restore this line and the import above. */}
            {/* <ShowSolutionButton showSolution={showSolution} setShowSolution={setShowSolution} /> */}
          </Col>
        </Row>
      )}
      style={{ padding: 0, margin: 0, textAlign: 'center' }}
    >

      <Space
        direction='vertical'
        style={{ display: 'flex' }}
      >
        <Progress
          percent={percent}
          type={progressType}
          status='active'
          strokeColor={{
            from: '#a3e635',
            to: '#84cc16'
          }}
        />

        <Board
          key={`game_${gameId}`}
          gameId={gameId}
          rows={puzzleFormatted}
          cellClick={openMenu}
          mistakes={mistakes}
        />

      </Space>

      <CellMenu
        cell={menuCell}
        mistake={menuCell ? mistakes[menuCell.index] : undefined}
        onPick={place}
        onClear={clearCell}
        onClose={() => setMenuCell(null)}
      />

    </Card>
  )
}

export default Play
